import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import notificationService from "data/notification"
import { diff_obj } from 'data/lib/diff'
import { push_undo } from 'data/lib/undo'
import { handleReferences } from 'data/lib/handle-references'
import incrementUnique from 'data/lib/increment-unique'
import { alphanum } from "components/table/lib/sort"
import { PromiseWatch } from "./lib/promise-helpers"
import { LineItem } from './line-items'

export type SystemLineItem = LineItem & {
  systeminput: 'system' | 'systemImmutable'
  systemlineitemid: number
}
// private module logic //

const id: string = "systemLineItems"
const initialState: ApiResult<SystemLineItem[]> = { id, state: "init", value: [] }
const systemLineItemsStream = new BehaviorSubject<ApiResult<SystemLineItem[]>>(initialState)
const systemLineItemsEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let systemLineItemsMap = {}
const _patches_ = PromiseWatch()

function clear(): void {
  systemLineItemsStream.next({ id, state: 'ok', value: [] })
  systemLineItemsMap = {}
}

function get(systemlineitemid: number): SystemLineItem {
  return systemLineItemsMap[systemlineitemid]
}

function refresh(): Promise<any> {
  const currentValue = systemLineItemsStream.getValue().value
  const propertyid = _.get(currentValue, '0.propertyid')
  if (_.isNil(propertyid)) return Promise.resolve()
  return fetchSystemLineItems(propertyid)
}

const _fetches_ = PromiseWatch()
function fetchSystemLineItems(
  propertyId: number,
  limit: number = 0,
  offset: number = 0
): Promise<SystemLineItem[] | { message: string }> {
  if (_fetches_.has(propertyId)) return _fetches_.first(propertyId)

  clear()
  systemLineItemsStream.next({ id, state: 'busy', value: systemLineItemsStream.getValue().value })

  let url = `systemlineitem?propertyid=eq.${propertyId}&order=type.asc,code.asc.nullsfirst`
  if (limit && offset >= 0) {
    url = `systemlineitem?propertyid=eq.${propertyId}&limit=${limit}&offset=${offset}&order=type.asc,code.asc.nullsfirst`
  }

  return _fetches_.watch(propertyId, Client.get(url)
  .then((records: SystemLineItem[]) => {
    _.forEach(records, (systemLineItems) => (systemLineItemsMap[systemLineItems.systemlineitemid] = systemLineItems))
    // merge results unique by systemlineitemid
    systemLineItemsStream.next({ id, state: 'ok', value: records })
    return records
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  }))
}

function create(propertyid: number): Promise<SystemLineItem | { message: string }> {
  const currentValue = systemLineItemsStream.getValue().value
  systemLineItemsStream.next({ id, state: 'busy', value: currentValue })

  const payload = { propertyid, inputtype: 'none', reportstatus: true }

  return Client.post(`systemlineitem`, payload)
    .then((systemLineItem: SystemLineItem) => {
      push_undo({
        undofunc: () => deleteOne(propertyid, systemLineItem.systemlineitemid),
        redofunc: () => create(propertyid)
      })
      systemLineItemsMap[systemLineItem.systemlineitemid] = systemLineItem
      // merge results unique by systemlineitemid
      const currentValue = systemLineItemsStream.getValue().value
      systemLineItemsStream.next({
        id,
        state: 'ok',
        value: _.unionBy([systemLineItem], currentValue, 'systemlineitemid')
      })
      systemLineItemsEventStream.next({ type: 'SYSTEMLINEITEM_CREATED', payload: systemLineItem })
      return systemLineItem
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      systemLineItemsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      fetchSystemLineItems(propertyid)
      return err
    })
}

function copy(propertyid:number, systemlineitemid:number, code:string) {
  const currentValue = systemLineItemsStream.getValue().value;
  systemLineItemsStream.next({ id, state: 'busy', value: currentValue });

  const payload = {table: 'systemlineitem', recordid: systemlineitemid, _synchronous: true, code: incrementUnique(code, "code", currentValue)};

  Client.post(`msg/svc_recordcopy`, payload)
    .then((systemLineItems: SystemLineItem[]) => {
      const newRecord = systemLineItems[0]
      push_undo({
        undofunc: () => deleteOne(propertyid, newRecord.systemlineitemid),
        redofunc: () => copy(propertyid, systemlineitemid, code)
      });
      systemLineItemsMap[newRecord.systemlineitemid] = newRecord
      // merge results unique by systemlineitemid
      const currentValue = systemLineItemsStream.getValue().value
      systemLineItemsStream.next({
        id,
        state: 'ok',
        value: _.unionBy([newRecord], currentValue, 'systemlineitemid').sort((a, b) => alphanum(a.code, b.code))
      })
      systemLineItemsEventStream.next({ type: 'SYSTEMLINEITEM_COPIED', payload: newRecord })
      return newRecord
    })
    .catch((err: { error: {text: string }}) => {
      // on error overwrite optimistic update
      systemLineItemsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.error.text, dismiss: 5000 })
      fetchSystemLineItems(propertyid)
      return err
    })
}

async function deleteOne(
  propertyid: number,
  systemlineitemid: number
): Promise<boolean | { message: string }> {
  const currentValue = systemLineItemsStream.getValue().value

  const deleteFn = () => deleteOne(propertyid, systemlineitemid)
  const refreshFn = () => fetchSystemLineItems(propertyid)
  if (!(await handleReferences('systemlineitem', systemlineitemid, {deleteFn, refreshFn}))) {
    return false
  }

  // filter out the deleted systemlineitemid
  const value = _(currentValue)
    .filter((p) => p.systemlineitemid !== systemlineitemid)
    .value()
  value.forEach((p) => (systemLineItemsMap[p.systemlineitemid] = p))

  systemLineItemsStream.next({ id, state: 'ok', value })

  // update the db
  return _patches_.all(systemlineitemid).then(() =>
  Client.del(`systemlineitem?systemlineitemid=eq.${systemlineitemid}`)
    .then((systemLineItems: SystemLineItem) => {
      delete systemLineItemsMap[systemLineItems.systemlineitemid]
      push_undo({
        undofunc: () => {
          Client.post('systemLineItems', systemLineItems)
          systemLineItemsMap[systemLineItems.systemlineitemid] = systemLineItems
          systemLineItemsStream.next({ id, state: 'ok', value: currentValue })
        },
        redofunc: () => deleteOne(propertyid, systemlineitemid)
      })
      // update already happened optimistically
      return true
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      systemLineItemsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({
        type: 'danger',
        message: err.message,
        dismiss: 5000
      })
      fetchSystemLineItems(propertyid)
      return err
    })
  )
}

function patch(systemLineItem: SystemLineItem): Promise<SystemLineItem | { message: string }> {
  const currentValue = systemLineItemsStream.getValue().value
  const systemlineitemid: number = systemLineItem.systemlineitemid
  const currentsystemLineItems = _.find(currentValue, { systemlineitemid })
  const changeset = diff_obj(systemLineItem, currentsystemLineItems)
  if (_.isEmpty(changeset)) return new Promise(() => currentValue)
  push_undo({
    undofunc: () => patch(currentsystemLineItems),
    redofunc: () => patch(systemLineItem)
  })

  // optimistic update
  systemLineItemsStream.next({
    id,
    state: 'ok',
    value: _.set(_.cloneDeep(currentValue), _.findIndex(currentValue, {systemlineitemid}), systemLineItem)
  })

  return _patches_.watch(systemlineitemid, Client.patch(`systemlineitem?systemlineitemid=eq.${systemlineitemid}`, changeset)
    .then((mf: SystemLineItem[]) => {
      _.forEach(mf, (systemLineItems) => (systemLineItemsMap[systemLineItems.systemlineitemid] = systemLineItems))
      return mf[0]
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      systemLineItemsStream.next({ id, state: 'error', error: err, value: currentValue })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      const propertyid = _.get(currentsystemLineItems, 'propertyid')
      if (propertyid) fetchSystemLineItems(propertyid)
      return err
    }))

}

// public module interface //

const systemLineItemsService = {
  stream: () => systemLineItemsStream.asObservable(),
  value: () => systemLineItemsStream.getValue().value,
  eventStream: () => systemLineItemsEventStream.asObservable(),
  clear,
  copy,
  create,
  deleteOne,
  get,
  fetchSystemLineItems,
  patch,
  refresh
}

export default systemLineItemsService
